import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { MENU_OPEN_WIDTH_VARIABLE, MENU_RAIL_WIDTH } from '@/shared/theme/railSlot'
import prefersReducedMotion from '@/shared/tools/prefersReducedMotion'

const useMenuWidthMotion = (enabled: boolean, expanded: boolean, onToggle: () => void) => {
  const [menu, setMenu] = useState<HTMLElement | null>(null)
  const wasFolded = useRef(!expanded)
  const animated = enabled && menu !== null && !prefersReducedMotion()

  const pinWidth = useCallback(
    (width: number) => menu?.style.setProperty(MENU_OPEN_WIDTH_VARIABLE, `${width}px`),
    [menu],
  )

  const toggle = useCallback(() => {
    if (animated && expanded && menu) {
      pinWidth(menu.getBoundingClientRect().width)
      menu.getBoundingClientRect()
    }
    onToggle()
  }, [animated, expanded, menu, onToggle, pinWidth])

  useLayoutEffect(() => {
    if (!expanded) {
      wasFolded.current = true
      return
    }
    if (!animated || !menu || !wasFolded.current) return

    menu.style.transitionProperty = 'background-color'
    menu.style.width = 'max-content'
    const openWidth = menu.getBoundingClientRect().width
    menu.style.width = MENU_RAIL_WIDTH
    menu.style.minWidth = '0px'
    pinWidth(openWidth)
    menu.getBoundingClientRect()
    menu.style.transitionProperty = ''
    menu.style.width = ''
    menu.style.minWidth = ''
  }, [animated, expanded, menu, pinWidth])

  useEffect(() => {
    if (!menu) return undefined

    const release = (event: TransitionEvent) => {
      if (event.target === menu && event.propertyName === 'width') menu.style.removeProperty(MENU_OPEN_WIDTH_VARIABLE)
    }
    menu.addEventListener('transitionend', release)
    menu.addEventListener('transitioncancel', release)

    return () => {
      menu.removeEventListener('transitionend', release)
      menu.removeEventListener('transitioncancel', release)
    }
  }, [menu])

  return { menuRef: setMenu, toggle }
}

export default useMenuWidthMotion
